import type { Candidate, DayView, ImpactDay } from '@tabel/shared/schemas/rules';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { BookmarkPlus, CalendarSearch, MoreHorizontal, Shuffle } from 'lucide-react';
import { useState } from 'react';
import { Badge, Button, Dropdown, IconButton, MenuItem, Skeleton } from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { ApiError, reportClientError } from '../../lib/errors';
import { ruDateTime } from '../../lib/format';
import { useRole } from '../../lib/role';
import type { ImpactState } from './ImpactLines';
import { shouldReport } from './LoadError';
import { PickDayDialog } from './PickDayDialog';
import { PinLink, usePin } from './pin';
import { LABEL, RESULT_TEXT, T, say } from './texts';

/*
 * Пример дня у правила (004, FR-004; contracts/ui.md, «Карточка правила»; research R6): ФИО, дата, проходы полосой,
 * часы в ячейке и одна строка «почему» — первая строка объяснения расчёта дня, как в карточке дня (FR-015). День
 * выбирает сервер в области роли («Расчёт дня»); часы считает движок, интерфейс только показывает. «Открыть расчёт
 * дня» открывает правую панель с карточкой дня только для чтения.
 */

/** День примера, который открывает правая панель. */
export interface OpenDay {
  personId: string;
  name: string;
  date: string;
}

export interface Mark {
  /** Минуты от 00:00 даты примера; следующие сутки — больше 1440. */
  min: number;
  dir: 'in' | 'out';
  /** «чч:мм»; в другую дату — «дд.мм чч:мм». */
  time: string;
  /** Время пришло «чч:мм» без даты. */
  clock?: true;
}

const DAY_MIN = 1440;

/** Дни от даты `date` до даты `дд.мм` того же года; через Новый год — соседний год. */
function daysTo(date: string, dd: string, mm: string): number {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  const y = Number(mm) === 1 && month === 12 ? year + 1 : Number(mm) === 12 && month === 1 ? year - 1 : year;
  return Math.round((Date.UTC(y, Number(mm) - 1, Number(dd)) - Date.parse(`${date}T00:00:00Z`)) / 86_400_000);
}

/**
 * Проход по местному времени объекта (specs/004 contracts/http-api.md, `DayView.passes[].at`): «чч:мм» — в дату
 * примера, «дд.мм чч:мм» — в другую дату; момент ISO — запасной вид. Непонятное время — `null`.
 */
export function markOf(p: DayView['passes'][number], date: string, tzOffset: string): Mark | null {
  const hhmm = /^(\d{2}):(\d{2})$/u.exec(p.at);
  if (hhmm) return { min: Number(hhmm[1]) * 60 + Number(hhmm[2]), dir: p.dir, time: p.at, clock: true };
  const dated = /^(\d{2})\.(\d{2}) (\d{2}):(\d{2})$/u.exec(p.at);
  if (dated) {
    const [, dd, mm, hh, mi] = dated as unknown as [string, string, string, string, string];
    return { min: daysTo(date, dd, mm) * DAY_MIN + Number(hh) * 60 + Number(mi), dir: p.dir, time: p.at };
  }
  if (!p.at.includes('T') || Number.isNaN(Date.parse(p.at))) return null;
  const s = ruDateTime(p.at, tzOffset); // «дд.мм.гггг чч:мм»
  const day = `${s.slice(6, 10)}-${s.slice(3, 5)}-${s.slice(0, 2)}`;
  const shift = Math.round((Date.parse(`${day}T00:00:00Z`) - Date.parse(`${date}T00:00:00Z`)) / 86_400_000);
  const time = s.slice(11, 16);
  const [h, m] = time.split(':').map(Number) as [number, number];
  return {
    min: shift * DAY_MIN + h * 60 + m,
    dir: p.dir,
    time: day === date ? time : `${s.slice(0, 5)} ${time}`,
  };
}

/**
 * Время «чч:мм» без даты идёт по порядку проходов: время меньше предыдущего — уже следующие сутки (смена через
 * полночь). У момента с датой сутки известны, его не сдвигаем.
 */
function unwrap(marks: readonly Mark[]): Mark[] {
  let days = 0;
  let prev = -1;
  return marks.map((m) => {
    if (!m.clock) return m;
    if (m.min + days * DAY_MIN < prev) days += 1;
    const min = m.min + days * DAY_MIN;
    prev = min;
    return { ...m, min };
  });
}

interface Span {
  from: Mark | null;
  to: Mark | null;
}

/** Отрезки «вход — выход» по порядку проходов; вход без выхода и выход без входа — открытые отрезки. */
function spansOf(marks: readonly Mark[]): Span[] {
  const out: Span[] = [];
  let open: Mark | null = null;
  for (const m of marks) {
    if (m.dir === 'in') {
      if (open) out.push({ from: open, to: null });
      open = m;
    } else {
      out.push({ from: open, to: m });
      open = null;
    }
  }
  if (open) out.push({ from: open, to: null });
  return out;
}

function spanText(s: Span): string {
  if (s.from && s.to) return say.passPair(s.from.time, s.to.time);
  if (s.from) return say.passNoExit(s.from.time);
  return say.passNoEntry(s.to?.time ?? '');
}

/** Полоса проходов: шкала с 06:00 до 22:00 или шире, если проходы за её краями. Только показ, без подсчёта часов. */
export function PassBar({ marks, spans }: { marks: readonly Mark[]; spans: readonly Span[] }) {
  if (marks.length === 0) return null;
  // Шкала — не шире окна расчёта дня (D−1…D+1): проход дальше стоит у края.
  const mins = marks.map((m) => Math.min(Math.max(m.min, -DAY_MIN), 2 * DAY_MIN));
  const start = Math.min(6 * 60, Math.floor(Math.min(...mins) / 60) * 60);
  const end = Math.max(22 * 60, Math.ceil((Math.max(...mins) + 1) / 60) * 60);
  const pct = (m: number) => ((Math.min(Math.max(m, start), end) - start) / (end - start)) * 100;
  const stepH = end - start > 18 * 60 ? 6 : 3;
  const ticks: number[] = [];
  for (let t = Math.ceil(start / (stepH * 60)) * stepH * 60; t <= end; t += stepH * 60) ticks.push(t);
  return (
    <div aria-hidden>
      <div className="relative h-[22px] overflow-hidden rounded-[6px] border border-line bg-surface">
        {spans.map((s) => {
          const from = s.from?.min ?? start;
          const to = s.to?.min ?? end;
          return (
            <span
              key={`${from}-${to}`}
              // Открытый отрезок (нет входа или выхода) — время неизвестно: пунктир, а не сплошная полоса присутствия
              // (приёмка 16.10.2026: «выход 16:43, входа нет» выглядел как день с утра).
              className={`absolute top-[5px] bottom-[5px] rounded-[3px] ${
                s.from && s.to ? 'bg-ok' : 'border border-dashed border-warn bg-warn/10'
              }`}
              style={{ left: `${pct(from)}%`, width: `${Math.max(pct(to) - pct(from), 0.5)}%` }}
            />
          );
        })}
      </div>
      <div className="relative h-3.5 text-[10.5px] leading-[14px] text-ink-3 tabular-nums">
        {ticks.map((t, i) => (
          <span
            key={t}
            className={`absolute ${i === 0 && pct(t) < 4 ? '' : pct(t) > 96 ? '-translate-x-full' : '-translate-x-1/2'}`}
            style={{ left: `${pct(t)}%` }}
          >
            {`${String((((t / 60) % 24) + 24) % 24).padStart(2, '0')}:00`}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Строки расчёта дня своего правила: «Обед: − 1 ч (на объекте больше 4 ч)»; нет — пусто. */
export function stepsText(day: DayView, group: string): string {
  return day.steps
    .filter((s) => s.group === group && s.value !== '—')
    .map((s) => `${s.label}: ${s.value}${s.note ? ` (${s.note})` : ''}.`)
    .join(' ');
}

/** Строки табеля «РВ» и «С» без распоряжения: часы в ячейке, ячейка выделена (Q-13; `timesheet-view.ts`). */
const NO_ORDER_ROWS: ReadonlySet<string> = new Set(['кандидат_РВ', 'кандидат_С']);

/**
 * Итог дня: часы в ячейке или состояние словами; часы без распоряжения — «ячейка выделена»; вопрос бригадиру — через
 * запятую.
 */
export function resultText(day: DayView): string {
  const state: Record<string, string> = {
    нет_выхода: RESULT_TEXT.noExit,
    no_exit: RESULT_TEXT.noExit,
    нет_входа: RESULT_TEXT.noEntry,
    no_entry: RESULT_TEXT.noEntry,
    на_объекте: RESULT_TEXT.onSite,
    on_site: RESULT_TEXT.onSite,
    пусто: RESULT_TEXT.empty,
    empty: RESULT_TEXT.empty,
    over_24h: RESULT_TEXT.over24,
    error: RESULT_TEXT.error,
  };
  // Строк с часами больше одной (ночь «Н» и «Я», «РВ») — раскладка рядом с итогом (приёмка 16.10.2026: «в табеле 9 ч»
  // в примере ночных часов не показывало «Н»).
  const rows = day.cells.filter((c) => c.hours > 0);
  const hours =
    rows.length > 1 ? `${say.hoursInCell(day.total)} ${say.cellRows(rows)}` : say.hoursInCell(day.total);
  const main = day.error ? RESULT_TEXT.error : (state[day.state] ?? hours);
  const noOrder = !day.error && day.cells.some((c) => NO_ORDER_ROWS.has(c.row) && c.hours > 0);
  const parts = [
    main,
    ...(noOrder ? [RESULT_TEXT.noOrder] : []),
    ...(day.questions.length > 0 ? [RESULT_TEXT.question] : []),
  ];
  return parts.join(', ');
}

export function DayExample({
  group,
  date,
  tzOffset,
  onOpen,
}: {
  group: string;
  /** Дата правил на странице. */
  date: string;
  tzOffset: string;
  onOpen: (day: OpenDay) => void;
}) {
  const { role } = useRole();
  const pin = usePin();
  const q = useQuery({
    queryKey: rulesKeys.example(role, group, date),
    queryFn: () => api.rulesExample(group, date),
    staleTime: 5 * 60_000,
  });
  if (q.isLoading)
    return (
      <div
        aria-busy="true"
        aria-label={LABEL.loading}
        className="space-y-2 rounded-[10px] border border-line bg-subtle px-3 py-2.5"
      >
        <Skeleton className="h-3 w-2/3" />
        <Skeleton className="h-[22px] w-full" />
      </div>
    );
  if (q.error) {
    const id = q.error instanceof ApiError ? q.error.requestId : null;
    return (
      <p className="text-[12.5px] text-ink-3">
        {T.exampleError}
        {id && ` ${say.errorCode(id)}`}
      </p>
    );
  }
  const ex = q.data;
  if (!ex) return null;
  if ('none' in ex) return <p className="text-[13px] text-ink-3">{T.noDays}</p>;

  const marks = unwrap(
    ex.day.passes.map((p) => markOf(p, ex.date, tzOffset)).filter((m): m is Mark => m !== null),
  ).sort((a, b) => a.min - b.min);
  const spans = spansOf(marks);
  const passes = spans.length > 0 ? spans.map(spanText).join(', ') : RESULT_TEXT.empty;
  return (
    <div
      role="group"
      aria-label={LABEL.example}
      className="flex flex-col gap-1.5 rounded-[10px] border border-line bg-subtle px-3 py-2.5 text-[13.5px]"
    >
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-ink-2">
        <span>
          {LABEL.examplePrefix} <b className="font-semibold text-ink">{say.exampleWho(ex.name, ex.date)}</b>
        </span>
        <span aria-hidden>·</span>
        <span className="tabular-nums">{passes}</span>
        {ex.golden && <Badge tone="accent">{LABEL.golden}</Badge>}
      </p>
      <PassBar marks={marks} spans={spans} />
      <p className="flex flex-wrap items-baseline gap-x-2">
        <span className="min-w-0 break-words text-ink-2">
          {[ex.day.why, stepsText(ex.day, group)].filter(Boolean).join(' ')}
        </span>
        <span className="ml-auto max-w-full text-right font-semibold break-words text-ink tabular-nums">
          → {resultText(ex.day)}
        </span>
      </p>
      <div className="flex flex-wrap items-center gap-x-4">
        <button
          type="button"
          onClick={() => onOpen({ personId: ex.personId, name: ex.name, date: ex.date })}
          className="-mx-1 rounded-control px-1 text-[12.5px] font-medium text-accent hover:underline max-sm:min-h-10"
        >
          {LABEL.openDay}
        </button>
        {/* Проверочный пример уже закреплён — закреплять нечего. «Сделать проверочным примером» — действие
            администратора, не часть ответа: в меню «…» примера (T106, план коррекции). */}
        {pin && !ex.golden && (
          <span className="ml-auto">
            <Dropdown
              align="end"
              width="w-[300px]"
              trigger={({ open, toggle }) => (
                <IconButton
                  icon={MoreHorizontal}
                  label={LABEL.exampleActions}
                  aria-haspopup="menu"
                  aria-expanded={open}
                  onClick={toggle}
                />
              )}
            >
              {(close) => (
                <MenuItem
                  icon={BookmarkPlus}
                  onClick={() => {
                    close();
                    pin({ personId: ex.personId, name: ex.name, date: ex.date }, ex.day);
                  }}
                >
                  {LABEL.pin}
                </MenuItem>
              )}
            </Dropdown>
          </span>
        )}
      </div>
    </div>
  );
}

// ── Пример в изменении (T064; FR-020–FR-022): «Сейчас / После публикации» ──

/** Проходы дня: полоса и строка «08:00–17:00, вход 19:00, выхода нет». */
export function passesOf(day: DayView, date: string, tzOffset: string) {
  const marks = unwrap(
    day.passes.map((p) => markOf(p, date, tzOffset)).filter((m): m is Mark => m !== null),
  ).sort((a, b) => a.min - b.min);
  const spans = spansOf(marks);
  return { marks, spans, text: spans.length > 0 ? spans.map(spanText).join(', ') : RESULT_TEXT.empty };
}

/** Одна сторона примера: часы в ячейке или состояние, вопрос бригадиру, строка «почему». */
function Side({
  title,
  day,
  accent,
  group,
  blocked,
}: {
  title: string;
  day: DayView | undefined;
  accent?: boolean;
  group: string;
  /** Почему день не посчитан (ошибка в значениях) — вместо дня. */
  blocked?: string;
}) {
  return (
    <div
      role="group"
      aria-label={title}
      className={`min-w-0 rounded-[8px] border bg-surface px-2.5 py-2 ${accent ? 'border-accent' : 'border-line'}`}
    >
      <p
        className={`text-[11.5px] font-semibold tracking-[0.05em] uppercase ${accent ? 'text-accent' : 'text-ink-3'}`}
      >
        {title}
      </p>
      {blocked ? (
        <p className="mt-0.5 text-[13px] break-words text-ink-2">{blocked}</p>
      ) : day ? (
        <>
          <p
            className={`mt-0.5 text-[17px] leading-[1.3] font-semibold tabular-nums ${accent ? 'text-accent' : 'text-ink'}`}
          >
            {resultText(day)}
          </p>
          <p className="mt-0.5 text-[13px] break-words text-ink-2">
            {[day.why, stepsText(day, group)].filter(Boolean).join(' ')}
          </p>
        </>
      ) : (
        <div aria-busy="true" aria-label={LABEL.loading} className="mt-1 space-y-1.5">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-full" />
        </div>
      )}
    </div>
  );
}

/**
 * Пример в изменении: день «Сейчас» (по действующим правилам, как ячейка табеля) и «После публикации» (с правкой);
 * их считает сервер (`POST /api/rules/day`). «Другой день, который изменится» — следующий день влияния по кругу;
 * «Выбрать день» — любой человек и дата в пределах прав. До 900 px стороны стоят друг под другом.
 */
export function EditExample({
  group,
  date,
  today,
  candidate,
  impact,
  tzOffset,
  onOpen,
}: {
  group: string;
  /** Дата правил на странице: пример правила — на эту дату. */
  date: string;
  /** Сегодня по объекту — самая поздняя дата «Выбрать день». */
  today: string;
  /** Правила с правкой; `null` — пока значение не исправлено. */
  candidate: Candidate | null;
  impact: ImpactState;
  tzOffset: string;
  onOpen: (day: OpenDay) => void;
}) {
  const { role } = useRole();
  const pin = usePin();
  const [own, setOwn] = useState<{ day: OpenDay; picked: boolean } | null>(null);
  const [index, setIndex] = useState(-1);
  const [picking, setPicking] = useState(false);
  const example = useQuery({
    queryKey: rulesKeys.example(role, group, date),
    queryFn: () => api.rulesExample(group, date),
    staleTime: 5 * 60_000,
  });
  const ex = example.data && !('none' in example.data) ? example.data : null;
  const day: OpenDay | null =
    own?.day ?? (ex ? { personId: ex.personId, name: ex.name, date: ex.date } : null);
  const cand = candidate ? JSON.stringify(candidate) : '';
  const calc = useQuery({
    queryKey: rulesKeys.day(role, day?.personId ?? '', day?.date ?? '', cand),
    queryFn: async () => {
      if (!day) throw new Error('день не выбран');
      try {
        return await api.rulesDay({
          personId: day.personId,
          date: day.date,
          ...(candidate ? { candidate } : {}),
        });
      } catch (e) {
        if (shouldReport(e)) reportClientError('Rules', 'example day', e);
        throw e;
      }
    },
    enabled: day !== null,
    // Та же правка на другом дне — заглушка; другое значение того же дня — прежние числа, пока считается новое.
    placeholderData: (prev, q) =>
      q?.queryKey[3] === day?.personId && q?.queryKey[4] === day?.date ? prev : undefined,
  });
  const days: readonly ImpactDay[] | null = impact.status === 'done' ? impact.result.thisChange.days : null;
  // Ошибка в значениях: «После публикации» с таким набором не посчитать — причина вместо «ошибки расчёта» (16.10.2026).
  const blocked =
    impact.status === 'done' && Array.isArray(impact.result.errors) && impact.result.errors.length > 0;

  const next = () => {
    if (!days || days.length === 0) return;
    const i = (index + 1) % days.length;
    const d = days[i] as ImpactDay;
    setIndex(i);
    setOwn({ day: { personId: d.personId, name: d.name, date: d.date }, picked: false });
  };

  if (example.isLoading && !own)
    return (
      <div
        aria-busy="true"
        aria-label={LABEL.loading}
        className="space-y-2 rounded-[10px] border border-line bg-subtle px-3 py-2.5"
      >
        <Skeleton className="h-3 w-2/3" />
        <Skeleton className="h-16 w-full" />
      </div>
    );

  const now =
    calc.data?.now ?? (ex && day?.personId === ex.personId && day.date === ex.date ? ex.day : undefined);
  const after = calc.data ? (calc.data.after ?? calc.data.now) : undefined;
  const passes = now && day ? passesOf(now, day.date, tzOffset) : null;
  const errorId = calc.error instanceof ApiError ? calc.error.requestId : null;

  return (
    <div
      role="group"
      aria-label={LABEL.example}
      className="flex flex-col gap-2 rounded-[10px] border border-line bg-subtle px-3 py-2.5 text-[13.5px]"
    >
      {/* Над «Сейчас / После публикации» — человек, дата и выбор дня (contracts/ui.md, «Изменение»). */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {day ? (
          <p className="flex min-w-0 flex-1 basis-[220px] flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-ink-2">
            <span>
              {LABEL.examplePrefix}{' '}
              <b className="font-semibold text-ink">{say.exampleWho(day.name, day.date)}</b>
            </span>
            {passes && (
              <>
                <span aria-hidden>·</span>
                <span className="tabular-nums">{passes.text}</span>
              </>
            )}
          </p>
        ) : (
          <p className="min-w-0 flex-1 text-[13px] text-ink-3">{T.noDays}</p>
        )}
        <span className="flex flex-wrap items-center gap-1 max-sm:w-full max-sm:[&>button]:flex-1">
          {days !== null && days.length > 0 && (
            <Button size="sm" variant="ghost" icon={Shuffle} onClick={next}>
              {LABEL.otherDay}
            </Button>
          )}
          <Button size="sm" variant="ghost" icon={CalendarSearch} onClick={() => setPicking(true)}>
            {LABEL.pickDay}
          </Button>
        </span>
      </div>
      {passes && <PassBar marks={passes.marks} spans={passes.spans} />}
      {day && (
        <div className="grid grid-cols-2 gap-2.5 max-tablet:grid-cols-1">
          <Side title={LABEL.now} day={now} group={group} />
          <Side
            title={LABEL.after}
            day={calc.error ? undefined : after}
            accent={!blocked && calc.data?.changed === true}
            group={group}
            blocked={blocked ? T.afterBlocked : undefined}
          />
        </div>
      )}
      {calc.error && (
        <p role="alert" className="text-[12.5px] text-bad">
          {/* Дня нет в табелях объекта (выбран вручную) — причина от сервера простыми словами; иначе — сбой. */}
          {calc.error instanceof ApiError && calc.error.status === 404 ? (
            calc.error.message
          ) : (
            <>
              {T.exampleCalcError}
              {errorId && <span className="block text-ink-3">{say.errorCode(errorId)}</span>}
            </>
          )}
        </p>
      )}
      {own?.picked && calc.data && !calc.data.changed && !calc.isPlaceholderData && (
        <p className="text-[13px] font-medium text-ink-2">{T.dayUnchanged}</p>
      )}
      {days !== null && days.length === 0 && (
        <p className="text-[13px] font-medium text-ink-2">{T.noDayChanges}</p>
      )}
      {day && (
        <div className="flex flex-wrap items-center gap-x-4">
          <button
            type="button"
            onClick={() => onOpen(day)}
            className="-mx-1 rounded-control px-1 text-[12.5px] font-medium text-accent hover:underline max-sm:min-h-10"
          >
            {LABEL.openDay}
          </button>
          {/* Ожидаемое примера — расчёт по действующим правилам, «Сейчас» (FR-051). */}
          {pin && !(ex?.golden && !own) && <PinLink onClick={() => pin(day, now)} />}
        </div>
      )}
      <PickDayDialog
        open={picking}
        date={today}
        onClose={() => setPicking(false)}
        onPick={(d) => {
          setPicking(false);
          setIndex(-1);
          setOwn({ day: d, picked: true });
        }}
      />
    </div>
  );
}
